import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import clsx from 'clsx';

import {
  getWishlistAsync,
  selectConfig,
  selectElementToWatchIDs,
  selectFormattedOrderItems,
  selectsortValue,
  selectWishlist,
  selectWishlistStatus,
  setElementToWatchIDs,
  setFormattedOrderItems,
  setFormattedOrderItemsStatus,
  setSortValue,
} from '../../store/wishlist/wishlistSlice';
import services from '../../store/wishlist/wishlistServices';
import { convertNumber, getContact, getLabelFallback, getUrl } from '@utils/utils';
import WishlistItem from './WishlistItem';
import Loader from '@components/Loader';
import { WISHLIST_PAGE_SIZE } from '@utils/const';
import { getMockFrameProperties } from '@utils/mockUtils';
import { OrderItem } from '@store/wishlist/wishlistInterfaces';

export default function Wishlist(): JSX.Element | null {
  const dispatch: any = useDispatch();

  const wishlist = useSelector(selectWishlist);
  const wishlistStatus = useSelector(selectWishlistStatus);
  const config = useSelector(selectConfig);
  const formattedItems = useSelector(selectFormattedOrderItems);
  const elementToWatchIDs = useSelector(selectElementToWatchIDs);
  const sortValue = useSelector(selectsortValue);

  useEffect(() => {
    var contactItemPromiseResolvedCount = 0;
    var contactItemPromiseToResolveTotal = 0;
    var isItemPromiseWithErrors = false;
    var newformattedItems = { ...formattedItems };

    wishlist?.savedItems?.forEach((elementToWatch, index) => {
      const isContact = elementToWatch.catentries.some(entry =>
        entry.catEntryType?.includes('CON')
      );
      if (isContact && !!elementToWatch?.catentries) {
        contactItemPromiseToResolveTotal++;
      }
    });

    wishlist?.savedItems?.forEach((elementToWatch, index) => {
      const isContact = elementToWatch.catentries.some(entry =>
        entry.catEntryType?.includes('CON')
      );

      if (isContact) {
        if (!!elementToWatch?.catentries) {
          //get all the frames in the cart
          const items = elementToWatch.catentries.filter(
            item =>
              item.catEntryType === 'SUN' ||
              item.catEntryType === 'EYE' ||
              item.catEntryType === 'LCON' ||
              item.catEntryType === 'RCON'
          );

          const itemPromise = new Promise(async (resolve, _) => {
            const item = items[0];

            //for each frame, get its lenses and addons
            const rightContactLenses = elementToWatch.catentries.find(
              i => i.catEntryType === 'RCON'
            );
            const leftContactLenses = elementToWatch.catentries.find(
              i => i.catEntryType === 'LCON'
            );

            //call to get the 'inner properties' of the frame
            let frame;

            let frameR;
            if (rightContactLenses) {
              const frameRightProductUrl =
                getUrl(config, 'PRODUCT_URL') + rightContactLenses.catEntryId;
              //call to get the 'inner properties' of the frame
              frameR = config.mockRequests
                ? await getMockFrameProperties()
                : await services.getItemProperties(frameRightProductUrl);
            }

            let frameL;
            if (leftContactLenses) {
              const frameLeftProductUrl =
                getUrl(config, 'PRODUCT_URL') + leftContactLenses.catEntryId;
              //call to get the 'inner properties' of the frame
              frameL = config.mockRequests
                ? await getMockFrameProperties()
                : await services.getItemProperties(frameLeftProductUrl);
            }

            if (frameR || frameL) frame = frameR || frameL;

            let contactResponse = {
              frame: {
                xitem_manufacturerName: frame.data.CatalogEntryView[0].manufacturer,
                catEntryId: item.catEntryId,
                parentCatEntryId: item.parentCatEntryId,
                category: item.catEntryType,
                contactLenses: true,
                totalAdjustment: elementToWatch.totalAmountOff || 0,
                xitem_basePrice: +item.listPrice / item.quantity,
                unitPrice: +item.offerPrice / item.quantity,
                finalPrice: !!item.listPrice ? convertNumber(item.listPrice) : null,
                finalDiscountPrice: !!item.offerPrice ? convertNumber(item.offerPrice) : null,
              },
              buyable: frame.data.CatalogEntryView[0].buyable ?? '',
              partNumber: frame.data.CatalogEntryView[0].partNumber ?? '',
            };

            if (frameL?.data) {
              contactResponse = {
                ...contactResponse,
                ...getContact(frameL?.data, leftContactLenses, 'left'),
              };
            }
            if (frameR?.data) {
              contactResponse = {
                ...contactResponse,
                ...getContact(frameR?.data, rightContactLenses, 'right'),
              };
            }

            resolve(contactResponse);
          });
          itemPromise
            .then((res: OrderItem[]) => {
              newformattedItems = {...newformattedItems, [elementToWatch.idList]: res};
            })
            .catch(err => {
              isItemPromiseWithErrors = true;
              console.error(err.status, err);
            })
            .finally(() => {
              contactItemPromiseResolvedCount++;
              //setFormattedOrderItems with all items in wishlist when all promises are done
              if(contactItemPromiseResolvedCount === contactItemPromiseToResolveTotal) {
                dispatch(setFormattedOrderItems(newformattedItems));
                dispatch(setFormattedOrderItemsStatus(isItemPromiseWithErrors ? 'ERROR' : 'SUCCESS'));
              }
            });
        } else {
          if (elementToWatch !== null) {
            dispatch(setFormattedOrderItemsStatus('ERROR'));
          }
        }
      }
    });
  }, [wishlist?.savedItems]);

  const handleLoadMore = () => {
    dispatch(
      getWishlistAsync({
        pageNumber: Math.ceil(wishlist.savedItems.length / WISHLIST_PAGE_SIZE) + 1,
        pageSize: WISHLIST_PAGE_SIZE,
        mergeItems: true,
        sortValue: sortValue,
      })
    );
  };

  if (wishlist !== null) {
    return (
      <div className={clsx('wishlist-container', process.env.NODE_ENV === 'development' && 'dev')}>
        {(wishlist?.savedItems?.length ?? 0) > 0 && (
          <>
            {wishlist.savedItems.map(item => (
              <WishlistItem item={item} key={item.idList} />
            ))}
            {wishlist.totalCount > WISHLIST_PAGE_SIZE &&
              wishlist.totalCount > (wishlist.savedItems.length ?? 0) && (
                <div className="wishlist-load-more">
                  <button
                    className={clsx(
                      'btn',
                      'btn-big',
                      config.brand === 'TO' ? 'btn-outline-primary' : 'btn-primary'
                    )}
                    onClick={handleLoadMore}
                  >
                    {getLabelFallback('WISHLIST_LOAD_MORE_BTN')}
                  </button>
                </div>
              )}
          </>
        )}
        {wishlistStatus === 'LOADING' && <Loader classes="main" />}
      </div>
    );
  }
  return null;
}
